import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {verify_token, changePassword} from '../core/_requests'
import {useNavigate, useParams} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'

const changePasswordValidation = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .matches(/^(?=.*[a-z])/, 'Must contain at least one lowercase character')
    .matches(/^(?=.*[A-Z])/, 'Must contain at least one uppercase character')
    .matches(/^(?=.*[0-9])/, 'Must contain at least one number')
    .matches(/^(?=.*[!@#$%^&*-])/, 'Must contain at least one special character')
    .min(8, 'Password must be at least 8 characters'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
})

export function ChangePassword() {
  const navigate = useNavigate()
  let {credentials} = useParams()
  const {data, isLoading, isError, isSuccess} = useQuery({
    queryKey: ['check_reset_token', {credentials}],
    queryFn: verify_token,
    enabled: !!credentials,
  })

  const initialVal = {
    password: '',
    confirm_password: '',
  }
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialVal,
    validationSchema: changePasswordValidation,
    onSubmit: async (values, {resetForm}) => {
      try {
        await changePassword({
          ...values,
          ...data,
        })
        resetForm()
        navigate('/auth')
      } catch (err) {
        console.error(err)
      }
    },
  })

  return (
    <>
      <p className='text-sm text-center mb-4'>
        {isLoading && <p>Checking Credentials...</p>}
        {isError && <p>Invalid Link</p>}
        {isSuccess && <p>Enter your new password</p>}
      </p>
      {isSuccess && (
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          id='kt_login_password_reset_form'
          onSubmit={formik.handleSubmit}
        >
          <div className='fv-row mb-10'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>New Password</label>
            <input
              type='password'
              placeholder=''
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {'is-invalid': formik.touched.password && formik.errors.password},
                {'is-valid': formik.touched.password && !formik.errors.password}
              )}
              disabled={formik.isSubmitting}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>{formik.errors.password}</div>
              </div>
            )}
          </div>

          <div className='fv-row mb-10'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>Confirm New Password</label>
            <input
              type='password'
              placeholder=''
              autoComplete='off'
              {...formik.getFieldProps('confirm_password')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {
                  'is-invalid': formik.touched.confirm_password && formik.errors.confirm_password,
                },
                {
                  'is-valid': formik.touched.confirm_password && !formik.errors.confirm_password,
                }
              )}
              disabled={formik.isSubmitting}
            />
            {formik.touched.confirm_password && formik.errors.confirm_password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>{formik.errors.confirm_password}</div>
              </div>
            )}
          </div>

          <div className='d-flex flex-column align-items-center pb-lg-0'>
            <button
              type='submit'
              id='kt_password_reset_submit'
              className='btn btn-lg btn-primary fw-bolder me-4'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!formik.isSubmitting ? (
                <span className='indicator-label'>Submit</span>
              ) : (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            <a href='/auth/login' className='text-decoration-none mt-10 w-100 text-start'>
              <small className='text-primary text-sm d-flex align-items-center'>
                <i className='bi bi-arrow-left me-2'></i>
                Back to login
              </small>
            </a>
          </div>
        </form>
      )}
    </>
  )
}
